/**
 * VGreen fit calculator (simplified).
 *
 * Inputs: wrist size, lug-to-lug, padding style, tail extension style.
 * Output: closest matching strap-length variant from a configured list,
 * plus the raw computed long/short pieces for reference.
 *
 * Modes:
 *  - data-mode="standalone" — used on /pages/calculator. The match is
 *    presented as advisory text only (no variant pre-select).
 *  - data-mode="product" — embedded inside the sizing aid on the
 *    product page. The "Use this size" button finds Dawn's
 *    variant-selects element and sets the matching strap-length
 *    option, dispatching a change event so the active variant
 *    refreshes.
 *
 * The variant list is read from a JSON script embedded by the snippet:
 *   <script type="application/json" data-vgreen-fitcalc-variants>
 *     [{"value":"55/100mm","short":55,"long":100}, ...]
 *   </script>
 *
 * No external dependencies. Pure vanilla JS.
 */
(function () {
  'use strict';

  // Hard-coded constants from the original calculator's math engine.
  // Don't change these without testing against Maya's actual fit.
  const SHORT_RATIO = 0.528; // bespoke ratio derived from wrist geometry
  const SPRING_BAR_OFFSET = 4; // mm — standard, non-integrated lugs
  const SHORT_PIECE_MIN = 60; // mm — manufacturing minimum
  const SHORT_PIECE_MAX = 150; // mm — manufacturing maximum
  const LONG_PIECE_MIN = 90; // mm — manufacturing minimum

  // Defaults that used to be customer-facing fields:
  //
  // PADDING_DEFAULT — wrap allowance for a standard padded leather
  //   (crocodile, alligator, ostrich, stingray, lizard — most VGreen
  //   leathers). Flat leathers (calfskin, suede) and heavy straps
  //   (bund, pilot) drift ~5mm in opposite directions; well within
  //   one buckle-hole step (~6.5mm), so the customer adjusts at the
  //   buckle without noticing. Customers ordering unusual padding
  //   typically write a Note anyway.
  //
  // TAIL_DEFAULT — Classic tail (passes both keepers neatly). The
  //   alternate "Extended" vintage tail is rarely requested and
  //   easy to specify in a Note.
  const PADDING_DEFAULT = 16; // mm
  const TAIL_DEFAULT = 48;    // mm

  function readVariants(root) {
    const node = root.querySelector(
      'script[type="application/json"][data-vgreen-fitcalc-variants]'
    );
    if (!node) return [];
    try {
      const list = JSON.parse(node.textContent);
      // Coerce to numbers in case Liquid stringified them.
      return list
        .map((v) => ({
          value: String(v.value || ''),
          short: Number(v.short),
          long: Number(v.long),
        }))
        .filter((v) => v.value && !isNaN(v.short) && !isNaN(v.long));
    } catch (e) {
      console.warn('[VGreen fitcalc] Could not parse variant list:', e);
      return [];
    }
  }

  function findClosestVariant(variants, targetShort, targetLong) {
    if (variants.length === 0) return null;
    let best = variants[0];
    let bestDiff = Infinity;
    let bestMaxPiece = Infinity;
    let bestSum = best.short + best.long;
    variants.forEach((v) => {
      const sDiff = Math.abs(v.short - targetShort);
      const lDiff = Math.abs(v.long - targetLong);
      const diff = sDiff + lDiff;            // overall closeness
      const maxPiece = Math.max(sDiff, lDiff); // worst single-piece error
      const sum = v.short + v.long;
      // Tie-break order:
      //   1. Smaller Manhattan distance (overall closeness).
      //   2. On tie: smaller max single-piece error (avoid a heavy
      //      one-sided overshoot — e.g. for a small wrist, prefer a
      //      balanced -5/+5 over an exact-short/+10-long).
      //   3. On further tie: larger sum — going up is safer than down
      //      because the buckle absorbs extra length.
      const better =
        diff < bestDiff
        || (diff === bestDiff && maxPiece < bestMaxPiece)
        || (diff === bestDiff && maxPiece === bestMaxPiece && sum > bestSum);
      if (better) {
        bestDiff = diff;
        bestMaxPiece = maxPiece;
        bestSum = sum;
        best = v;
      }
    });
    return { variant: best, diff: bestDiff };
  }

  function compute(inputs) {
    const { wrist, lugToLug } = inputs;

    // From the original calculator's math engine. Don't refactor.
    const pinToPin = lugToLug - SPRING_BAR_OFFSET;
    const wrappedLen = wrist + PADDING_DEFAULT - pinToPin;

    let shortPiece = Math.round((wrappedLen * SHORT_RATIO) / 5) * 5;
    shortPiece = Math.min(Math.max(shortPiece, SHORT_PIECE_MIN), SHORT_PIECE_MAX);

    const targetDist = wrappedLen - shortPiece;
    let longPiece = Math.round((targetDist + TAIL_DEFAULT) / 5) * 5;
    if (longPiece < LONG_PIECE_MIN) longPiece = LONG_PIECE_MIN;

    return { shortPiece, longPiece };
  }

  function applyLengthToProductForm(targetValue) {
    // Strap length switched from variant axis to LIP on 2026-05-12.
    // Find the <select name="properties[Strap length]"> rendered by
    // the configurator snippet and set its value. Property name is
    // consistent across custom_strap, notched, apple_watch, and
    // simple_strap configurators.
    const sel = document.querySelector('select[name="properties[Strap length]"]');
    if (!sel) return false;
    const option = Array.from(sel.options).find((o) => o.value === targetValue);
    if (!option) return false;
    if (sel.value === option.value) return true; // already set
    sel.value = option.value;
    sel.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function init(root) {
    const mode = root.dataset.mode === 'standalone' ? 'standalone' : 'product';
    const variants = readVariants(root);

    const wristInput   = root.querySelector('[data-vg-fc="wrist"]');
    const lugInput     = root.querySelector('[data-vg-fc="lug"]');
    const goBtn        = root.querySelector('[data-vg-fc="go"]');
    const errorBox     = root.querySelector('[data-vg-fc="error"]');
    const resultBox    = root.querySelector('[data-vg-fc="result"]');
    const resultSizeEl = root.querySelector('[data-vg-fc="result-size"]');
    const resultDetail = root.querySelector('[data-vg-fc="result-detail"]');
    const useBtn       = root.querySelector('[data-vg-fc="use"]');
    const unitMmBtn    = root.querySelector('[data-vg-fc="unit-mm"]');
    const unitInBtn    = root.querySelector('[data-vg-fc="unit-in"]');
    const wristHint    = root.querySelector('[data-vg-fc="wrist-hint"]');

    if (!wristInput || !lugInput || !goBtn) return;

    // Wrist unit state — 'mm' or 'in'. Default in (60% of sales are US).
    let wristUnit = 'in';

    function setWristUnit(unit) {
      if (unit === wristUnit) return;
      const prev = wristUnit;
      wristUnit = unit;

      // Convert the current displayed value
      const current = parseFloat(wristInput.value);
      if (!isNaN(current)) {
        if (prev === 'mm' && unit === 'in') {
          wristInput.value = (current / 25.4).toFixed(1);
        } else if (prev === 'in' && unit === 'mm') {
          wristInput.value = Math.round(current * 25.4);
        }
      }

      // Update input attributes + hint
      if (unit === 'in') {
        wristInput.placeholder = '例: 6.7';
        wristInput.min = '4';
        wristInput.max = '12';
        wristInput.step = '0.1';
        if (wristHint) wristHint.textContent = 'インチ単位（6.7インチ ≈ 170mm）。';
      } else {
        wristInput.placeholder = '例: 170';
        wristInput.min = '100';
        wristInput.max = '300';
        wristInput.step = '1';
        if (wristHint) wristHint.textContent = 'ミリ単位（170mm ≈ 6.7インチ）。';
      }

      // Update button states
      if (unitMmBtn && unitInBtn) {
        const mmActive = unit === 'mm';
        unitMmBtn.classList.toggle('is-active', mmActive);
        unitInBtn.classList.toggle('is-active', !mmActive);
        unitMmBtn.setAttribute('aria-pressed', mmActive ? 'true' : 'false');
        unitInBtn.setAttribute('aria-pressed', !mmActive ? 'true' : 'false');
      }

      clearResult();
    }

    if (unitMmBtn) unitMmBtn.addEventListener('click', () => setWristUnit('mm'));
    if (unitInBtn) unitInBtn.addEventListener('click', () => setWristUnit('in'));

    function showError(msg) {
      errorBox.textContent = msg;
      errorBox.classList.add('is-visible');
      resultBox.classList.remove('is-visible');
    }

    function hideError() {
      errorBox.classList.remove('is-visible');
    }

    function clearResult() {
      resultBox.classList.remove('is-visible');
      hideError();
      if (useBtn) {
        useBtn.classList.remove('is-applied');
        useBtn.textContent = useBtn.dataset.labelDefault || 'このサイズを使う';
        useBtn.disabled = false;
      }
    }

    function run() {
      hideError();

      const wristRaw = parseFloat(wristInput.value);
      const lugToLug = parseFloat(lugInput.value);

      if (!wristRaw || !lugToLug) {
        showError('手首のサイズと時計のラグ間距離の両方をご入力ください。');
        return;
      }

      // Convert wrist to mm if entered in inches
      const wrist = wristUnit === 'in' ? wristRaw * 25.4 : wristRaw;

      if (wrist < 130 || wrist > 300) {
        const unitLabel = wristUnit === 'in'
          ? 'インチ（目安 5.5〜10インチ）'
          : 'ミリ（目安 140〜230mm）';
        showError('手首のサイズが通常と異なるようです。測定単位は' + unitLabel + 'です。再度ご確認ください。');
        return;
      }
      if (lugToLug < 30 || lugToLug > 65) {
        showError('ラグ間距離が通常と異なるようです。再度ご確認ください（目安 36〜55mm）。');
        return;
      }

      const { shortPiece, longPiece } = compute({ wrist, lugToLug });

      if (variants.length === 0) {
        // Fallback: just show the raw computation.
        resultSizeEl.textContent = shortPiece + '/' + longPiece + 'mm';
        resultDetail.textContent =
          '測定値に基づく最も近い標準サイズです。商品ページでこのサイズをお選びください。';
        if (useBtn) useBtn.style.display = 'none';
        resultBox.classList.add('is-visible');
        return;
      }

      const match = findClosestVariant(variants, shortPiece, longPiece);
      if (!match) {
        showError("標準サイズが見つかりませんでした。/pages/custom-watch-strap-length-calculator の計算機をお試しいただくか、お問い合わせください。");
        return;
      }

      resultSizeEl.textContent = match.variant.value;
      resultDetail.textContent = '';

      if (useBtn) {
        useBtn.style.display = '';
        useBtn.classList.remove('is-applied');
        useBtn.textContent = useBtn.dataset.labelDefault || 'このサイズを使う';
        useBtn.disabled = false;
        useBtn.dataset.targetValue = match.variant.value;
      }

      resultBox.classList.add('is-visible');
    }

    // Wire up listeners.
    goBtn.addEventListener('click', run);

    [wristInput, lugInput].forEach((el) => {
      el.addEventListener('input', clearResult);
    });

    if (mode === 'product' && useBtn) {
      useBtn.addEventListener('click', () => {
        const target = useBtn.dataset.targetValue;
        if (!target) return;
        const ok = applyLengthToProductForm(target);
        if (ok) {
          useBtn.classList.add('is-applied');
          useBtn.textContent = '✓ 選択しました — ストラップ長のメニューをご確認ください';
          useBtn.disabled = true;
        } else {
          // Couldn't find a matching option — fall back to instructive text.
          useBtn.textContent = 'ストラップ長のメニューで「' + target + '」をお選びください';
        }
      });
    }
  }

  document.querySelectorAll('.vgreen-fit-calc').forEach(init);
})();
